import { z } from 'zod'
import { uuidSchema } from '../validators/uuid.validator.js'
import {
  MAX_VIDEO_SECONDS,
  type VideoOwnerKey,
} from '../constants/documents.js'

/**
 * Asking for somewhere to put a video.
 *
 * Only a filename and a duration, because that is genuinely all we know before
 * the bytes exist. Stream assigns the identifier and re-encodes the file, so
 * neither a content type nor a byte size would survive contact with it.
 *
 * `durationSeconds` is a claim the browser reads off the file, used to refuse
 * an hour-long recording before it is uploaded rather than after. Stream
 * enforces the real limit itself.
 */
const videoUploadFields = {
  fileName: z
    .string()
    .trim()
    .min(1, 'File name is required')
    .max(255)
    .refine(
      (value) => !value.includes('/') && !value.includes('\\'),
      'File name cannot contain a path',
    ),
  durationSeconds: z
    .number()
    .int()
    .positive('That video appears to be empty')
    .max(
      MAX_VIDEO_SECONDS,
      `Videos must be ${String(Math.floor(MAX_VIDEO_SECONDS / 60))} minutes or shorter`,
    )
    .optional(),
}

export const createTreeVideoInputSchema = z
  .object({
    ...videoUploadFields,
    /**
     * The visit a video of a tree is taken on, as for a photo of one: the
     * phone's, null for none, left out for the site's current tree visit.
     */
    visitId: uuidSchema.nullish(),
  })
  .strict()

export type CreateTreeVideoInput = z.infer<typeof createTreeVideoInputSchema>

/** Listing a tree's videos: only those taken on one visit, when it says which. */
export const listTreeVideosQuerySchema = z
  .object({ visitId: uuidSchema.optional() })
  .strict()

export type ListTreeVideosQuery = z.infer<typeof listTreeVideosQuerySchema>

/**
 * What a video is of: a tree, or one irrigation record. Null, where a caller
 * accepts it, is a stock clip for a problem type.
 *
 * One variant per {@link VideoOwnerKey}. The sweep that deletes stock clips
 * and the survey that lists videos both start from `VIDEO_OWNER_KEYS`; a
 * hand-written union would let a new column land in one and not the other.
 */
export type VideoOwner = {
  [Key in VideoOwnerKey]: { [P in Key]: string }
}[VideoOwnerKey]

/** The id a video belongs to. One owner names exactly one record. */
export function videoOwnerId(owner: VideoOwner): string {
  if ('treeId' in owner) return owner.treeId
  if ('waterSourceId' in owner) return owner.waterSourceId
  if ('irrigationTimerId' in owner) return owner.irrigationTimerId
  if ('irrigationEquipmentId' in owner) return owner.irrigationEquipmentId
  return owner.irrigationProblemId
}

/** An irrigation record: every video owner except a tree. */
type IrrigationOwnerKey = Exclude<VideoOwnerKey, 'treeId'>

const IRRIGATION_OWNER_KEYS = [
  'waterSourceId',
  'irrigationTimerId',
  'irrigationEquipmentId',
  'irrigationProblemId',
] as const

/** An irrigation record, named by exactly one of these. */
const irrigationOwnerShape = {
  waterSourceId: uuidSchema.optional(),
  irrigationTimerId: uuidSchema.optional(),
  irrigationEquipmentId: uuidSchema.optional(),
  irrigationProblemId: uuidSchema.optional(),
}

/**
 * The query and the "exactly one record" check name the same irrigation
 * records `VIDEO_OWNER_KEYS` does, minus a tree. A key added to that list
 * and forgotten here would list videos the sweep still treats as stock, and
 * delete them.
 */
type SameMembers<A, B> =
  (<T>() => T extends A ? 1 : 2) extends <T>() => T extends B ? 1 : 2
    ? true
    : false

function assertSameMembers<A, B>(
  same: SameMembers<A, B> extends true ? true : never,
): void {
  void same
}

assertSameMembers<(typeof IRRIGATION_OWNER_KEYS)[number], IrrigationOwnerKey>(
  true,
)
assertSameMembers<keyof typeof irrigationOwnerShape, IrrigationOwnerKey>(true)

function namesOneRecord(
  value: Partial<Record<IrrigationOwnerKey, string | undefined>>,
): boolean {
  const named = IRRIGATION_OWNER_KEYS.filter((key) => value[key] !== undefined)
  return named.length === 1
}

const ONE_RECORD = 'Name exactly one irrigation record.'

/**
 * Listing one irrigation record's videos. The owner is a query parameter,
 * where a tree's is in the path, because one route serves four kinds of
 * record.
 */
export const listIrrigationVideosQuerySchema = z
  .object(irrigationOwnerShape)
  .strict()
  .refine(namesOneRecord, ONE_RECORD)

export type ListIrrigationVideosQuery = z.infer<
  typeof listIrrigationVideosQuerySchema
>

/** Asking for somewhere to put a video of one irrigation record. */
export const createIrrigationVideoInputSchema = z
  .object({ ...videoUploadFields, ...irrigationOwnerShape })
  .strict()
  .refine(namesOneRecord, ONE_RECORD)

export type CreateIrrigationVideoInput = z.infer<
  typeof createIrrigationVideoInputSchema
>
